import React from 'react'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import DateTime from 'react-datetime'
import debounce from 'debounce-promise'
import {FormattedMessage, useIntl} from "react-intl";
import messages from "./messages";

const Delivery = ({
  state: {
    comments = '',
    handlingLanguage = null,
    wordCount = null,
    playOutMinutes = null,
    deadline = null,
    expected = null,
    priority = null,
    complex = null,
    customerReference = null,
    contacts: { coordinator, ...others } = {},
    companyPoRequired = false
  },
  setState
}) => {
  const { formatMessage } = useIntl()
  const priorities = {
    MEDIUM: { value: 'MEDIUM', label: formatMessage(messages.medium), className: '' },
    LOW: { value: 'LOW', label: formatMessage(messages.low), className: '' },
    HIGH: { value: 'HIGH', label: formatMessage(messages.high), className: 'text-red-700 font-bold' },
  }

  const handlingLanguages = {
    '17': { value: '17', label: formatMessage(messages.english), language: true },
    '14': { value: '14', label: formatMessage(messages.danish), language: true },
    '23' :{ value: '23', label: formatMessage(messages.german), language: true  }
  }
  const loadOptions = debounce((query) => {
    return new Promise((resolve, reject) => {
      fetch(`/rest/v2/coordinator?pageSize=25&search[name]=${query}`)
        .then(response => response.json())
        .then((coordinators) => {
          resolve([
            ...Object.values(handlingLanguages),
            ...coordinators.map(({ uid, name }) => ({ value: uid, label: name, coordinator: true })),
          ])
        })
        .catch(reject)
    })
  }, 250)

  const onHandlingChange = (props) => {
    const { value, label, language } = props
    if (language) {
      setState((state) => ({ ...state, coordinator: 0, handlingLanguage: value, contacts: { ...others, coordinator: null } }))
    } else {
      setState((state) => ({ ...state, coordinator: value, handlingLanguage: null, contacts: { ...others, coordinator: { id: value, name: label } } }))
    }
  }

  const defaultDateToSpecificHour = (value, defaultHour) => {
    let date = new Date(value);
    if (date.getHours() == '00' && date.getMinutes() == '00') {
      date.setHours(defaultHour);
    }
    return date;
  }

  const handlingValue = coordinator
    ? { label: coordinator.name, value: coordinator.id, coordinator: true }
    : handlingLanguages[handlingLanguage]

  return (
    <div className="flex justify-between my-4">
      <div className="grow">
        <div className="text-gray-500 mb-2">Delivery Note</div>
        <textarea
          type="text"
          className="rounded-sm border border-gray-400 px-4 py-3 w-full h-32"
          onChange={({ target }) => setState((state) => ({ ...state, comments: target.value }))}
          value={comments}
        />
      </div>

      <div className="w-64 ml-3">
        <div className="text-gray-500 mb-2">
          <FormattedMessage {...messages.purchaseOrder}/>
        </div>
        <input
          type="text"
          className={`rounded-sm border ${ companyPoRequired ?  'border-red-400' : 'border-gray-400'} px-4 py-3 w-full`}
          
          onChange={({ target }) => setState((state) => ({ ...state, customerReference: target.value }))}
          value={customerReference}
        />
        <div className="text-gray-500 mb-2">Priority</div>
        <Select
          value={priorities[priority]}
          onChange={({ value }) => setState((state) => ({ ...state, priority: value }))}
          openMenuOnFocus
          options={Object.values(priorities)}
          components={{
            SingleValue: ({ className, data, ...props }) => (
              <div className={`${className} ${data.className}`} data={data} {...props} />
            )
          }}
        />
      </div>

      <div className="w-64 ml-3">
        <div className={`mb-2 ${deadline ? 'text-gray-500 ' : 'text-red-400'}`}>Deadline *</div>
        <DateTime
          value={deadline}
          onChange={(value) => setState((state) => ({ ...state, deadline: defaultDateToSpecificHour(value, 17) }))}
          className={`rounded-sm border ${ deadline ? 'border-gray-400' : 'border-red-400'} px-4 py-3 w-full`}
          closeOnTab
          closeOnSelect
        />
        <div className="text-gray-500 mb-2"><FormattedMessage {...messages.expected}/></div>
        <DateTime
          value={expected}
          onChange={(value) => setState((state) => ({ ...state, expected: defaultDateToSpecificHour(value, 17) }))}
          onBlur={() => !expected && setState((state) => ({ ...state, expected: deadline }))}
          className="rounded-sm border border-gray-400 px-4 py-3 w-full"
          closeOnTab
          closeOnSelect
        />
      </div>

      <div className="w-40 ml-3">
        <div className="text-gray-500 mb-2">Complex</div>
        <Select
          value={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }][complex ? 0 : 1]}
          onChange={({ value }) => setState((state) => ({ ...state, complex: value }))}
          openMenuOnFocus
          options={[{ value: true, label: formatMessage(messages.yes)}, { value: false, label: formatMessage(messages.no) }]}
        />

        <div className="text-gray-500 mb-2">Handling</div>
        <AsyncSelect
          value={handlingValue}
          onChange={onHandlingChange}
          cacheOptions
          loadOptions={loadOptions}
          openMenuOnFocus
          defaultOptions
          noOptionsMessage={({ inputValue }) => `'${formatMessage(messages.noResults)}' '${inputValue}'`}
          loadingMessage={({ inputValue }) => `'${formatMessage(messages.searchingFor)}' '${inputValue}'..`}
          placeholder={formatMessage(messages.handling)}
        />
      </div>

      <div className="w-40 ml-3">
        <div className="text-gray-500 mb-2">
          <FormattedMessage {...messages.wordCount}/>
        </div>
        <input
          type="number"
          className="rounded-sm border border-gray-400 px-4 py-3 w-full"
          onChange={({ target }) => setState((state) => ({ ...state, wordCount: parseInt(target.value) }))}
          value={wordCount}
        />
        <div className="text-gray-500 mb-2">
          <FormattedMessage {...messages.playOutMinutes}/>
        </div>
        <input
          type="number"
          className="rounded-sm border border-gray-400 px-4 py-3 w-full"
          onChange={({ target }) => setState((state) => ({ ...state, playOutMinutes: parseInt(target.value) }))}
          value={playOutMinutes}
        />
      </div>
    </div>
  )
}

export default Delivery
